import React from 'react';
import { Bar, BarChart, Cell, Label, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatMoney } from '../../../lib/formatMoney';
import { DEFAULT_CHART_PALETTE } from '../../../lib/chartColors';
import { distributeIntegerPercents } from '../../../lib/sharePercents';
import { useTheme } from '../../../context/ThemeContext';

function truncateLabel(value, maxLength = 14) {
  if (!value || value.length <= maxLength) {
    return value;
  }
  return `${value.slice(0, maxLength)}...`;
}

function RankingTooltip({ active, payload, valueLabel = 'Revenue', formatValue, accentColor }) {
  if (!active || !payload?.length) {
    return null;
  }

  const row = payload[0]?.payload;
  if (!row) {
    return null;
  }

  const rawValue = row.rawValue ?? row.value ?? 0;
  const formattedValue = formatValue ? formatValue(rawValue) : formatMoney(rawValue);

  return (
    <div className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-[11px] shadow-sm">
      <div className="mb-1 font-semibold text-[#201C35]">{row.name}</div>
      <div className="flex items-center justify-between gap-3 text-slate-600">
        <span>{valueLabel}</span>
        <span className="font-semibold" style={{ color: accentColor }}>{formattedValue}</span>
      </div>
      {typeof row.share === 'number' ? (
        <div className="mt-1 flex items-center justify-between gap-3 text-slate-600">
          <span>Share</span>
          <span className="font-semibold text-slate-700">{row.share}%</span>
        </div>
      ) : null}
    </div>
  );
}

export function buildRankingChartData(items, nameKey, valueKey, palette = DEFAULT_CHART_PALETTE) {
  const rawValues = items.map((item) => Number(item[valueKey] || 0));
  const shares = distributeIntegerPercents(rawValues);

  return items.map((item, index) => {
    const rawValue = rawValues[index];
    return {
      name: item[nameKey] || 'Unknown',
      value: rawValue,
      rawValue,
      share: shares[index],
      fill: palette[index % palette.length],
    };
  });
}

export function DefaultRankingBarList({ data, formatValue }) {
  const maxValue = data.length ? Math.max(...data.map((item) => item.rawValue)) : 1;

  return (
    <div className="space-y-2.5">
      {data.map((item) => {
        const percent = maxValue > 0 ? Math.round((item.rawValue / maxValue) * 100) : 0;

        return (
          <div
            key={item.name}
            className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_2fr_auto] sm:items-center"
          >
            <span className="truncate text-[12px] font-medium text-slate-700" title={item.name}>
              {item.name}
            </span>
            <div className="h-2.5 rounded-full bg-slate-100">
              <div
                className="h-full rounded-full"
                style={{ width: `${percent}%`, backgroundColor: item.fill }}
              />
            </div>
            <span className="text-[10px] font-semibold text-slate-500">
              {formatValue(item.rawValue)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function RankingChartLegend({
  data,
  compact,
  leadingTitle = 'Leading item',
  leadingCaption = 'Primary contributor',
  formatItemValue,
}) {
  const { primaryColor } = useTheme();
  const lead = data[0];
  const formatValue = formatItemValue || ((item) => `${item.share}%`);

  return (
    <div className={compact ? 'space-y-1.5 text-[10px] text-slate-600' : 'space-y-3'}>
      {lead ? (
        <div
          className="rounded-xl px-3 py-2.5 text-white shadow-sm"
          style={{ backgroundColor: primaryColor || 'var(--brand-primary, #1A428A)' }}
        >
          <div className="text-[9px] uppercase tracking-[0.18em] text-white/75">{leadingTitle}</div>
          <div className="mt-1 flex items-end justify-between gap-3">
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-white" title={lead.name}>
                {lead.name}
              </div>
              <div className="text-[10px] text-white/70">{leadingCaption}</div>
            </div>
            <div className="text-right text-sm font-semibold text-white">{formatValue(lead)}</div>
          </div>
        </div>
      ) : null}

      <div className="space-y-2 text-[11px] text-slate-600">
        {data.map((item) => (
          <div
            key={item.name}
            className="flex items-center justify-between gap-3 border-b border-slate-100 pb-1.5 last:border-b-0 last:pb-0"
          >
            <div className="flex min-w-0 items-center gap-2">
              <span className="h-2 w-2 flex-shrink-0 rounded-sm" style={{ backgroundColor: item.fill }} />
              <span className="truncate" title={item.name}>{item.name}</span>
            </div>
            <span className="font-semibold text-slate-500">{formatValue(item)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export function RankingBarChart({
  data,
  currencyCode = 'DKK',
  compact = false,
  valueLabel = 'Revenue',
  formatValue,
  ariaLabel = 'Ranking bar chart',
  leadingTitle = 'Leading item',
  leadingCaption = 'Primary contributor',
}) {
  const { chartLine } = useTheme();
  const tooltipFormatter = formatValue || ((value) => formatMoney(value, { currency: currencyCode }));
  const chartHeight = compact ? 'h-[200px]' : 'h-[260px]';

  return (
    <div
      className={`grid items-center gap-3 sm:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] ${compact ? 'min-h-[200px]' : 'min-h-[260px]'}`}
      role="img"
      aria-label={ariaLabel}
    >
      <div className={chartHeight}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: compact ? 36 : 28 }}>
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 10, fill: '#475569' }}
              tickFormatter={(value) => truncateLabel(value, compact ? 10 : 12)}
              interval={0}
              angle={compact ? -28 : -18}
              textAnchor="end"
              height={compact ? 52 : 44}
            />
            <YAxis hide />
            <Tooltip
              cursor={{ fill: chartLine.cursorFill }}
              content={
                <RankingTooltip
                  valueLabel={valueLabel}
                  formatValue={tooltipFormatter}
                  accentColor={chartLine.tooltipAccent}
                />
              }
            />
            <Bar dataKey="value" radius={[6, 6, 0, 0]} maxBarSize={compact ? 36 : 48} isAnimationActive={false}>
              {data.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <RankingChartLegend
        data={data}
        compact={compact}
        leadingTitle={leadingTitle}
        leadingCaption={leadingCaption}
        formatItemValue={(item) => tooltipFormatter(item.rawValue)}
      />
    </div>
  );
}

function PieCenterLabel({ viewBox, totalShare }) {
  if (!viewBox || !('cx' in viewBox) || !('cy' in viewBox)) {
    return null;
  }

  return (
    <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
      <tspan x={viewBox.cx} y={viewBox.cy} className="fill-slate-900 text-2xl font-bold">
        {totalShare}%
      </tspan>
      <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 18} className="fill-slate-500 text-[11px]">
        Total Mix
      </tspan>
    </text>
  );
}

export function RankingPieChart({
  data,
  currencyCode = 'DKK',
  compact = false,
  valueLabel = 'Revenue',
  formatValue,
  ariaLabel = 'Ranking pie chart',
  leadingTitle = 'Leading item',
  leadingCaption = 'Primary contributor',
}) {
  const { chartLine } = useTheme();
  const tooltipFormatter = formatValue || ((value) => formatMoney(value, { currency: currencyCode }));
  const chartHeight = compact ? 'min-h-[200px]' : 'min-h-[240px]';
  const totalShare = data.length && data.some((item) => item.share > 0) ? 100 : 0;

  return (
    <div
      className={`grid items-center gap-3 sm:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] ${chartHeight}`}
      role="img"
      aria-label={ariaLabel}
    >
      <div className={compact ? 'h-[180px]' : 'h-[220px]'}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Tooltip
              content={
                <RankingTooltip
                  valueLabel={valueLabel}
                  formatValue={tooltipFormatter}
                  accentColor={chartLine.tooltipAccent}
                />
              }
            />
            <Pie
              data={data}
              dataKey="share"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={compact ? 48 : 54}
              outerRadius={compact ? 72 : 78}
              paddingAngle={2}
              strokeWidth={6}
              isAnimationActive={false}
            >
              {data.map((entry) => (
                <Cell key={entry.name} fill={entry.fill} />
              ))}
              <Label content={({ viewBox }) => <PieCenterLabel viewBox={viewBox} totalShare={totalShare} />} />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>

      <RankingChartLegend
        data={data}
        compact={compact}
        leadingTitle={leadingTitle}
        leadingCaption={leadingCaption}
        formatItemValue={(item) => `${item.share}%`}
      />
    </div>
  );
}
